import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Camera, Loader2, X } from "lucide-react";
import { uploadToCloudinary, cloudinaryThumb } from "@/lib/cloudinary";
import { toast } from "sonner";

/**
 * Modal rápido para añadir un Gasto.
 * Props:
 *  - open / onOpenChange
 *  - rutaId?: si se pasa, pre-fija categoría=ruta y vincula esa ruta
 *  - peticionId?: si se pasa, pre-fija categoría=proyecto y vincula esa petición
 *  - onSaved?: () => void  (cb al guardar)
 *  - title?
 */
export default function QuickGastoModal({
  open, onOpenChange,
  rutaId, peticionId,
  onSaved, title,
}) {
  const todayISO = () => new Date().toISOString().slice(0, 10);
  const defaultCategoria = peticionId ? "proyecto" : rutaId ? "ruta" : "general";

  const initial = {
    fecha: todayISO(),
    categoria_gasto: defaultCategoria,
    tipo_gasto: "Otros gastos",
    concepto: "",
    importe: "",
    metodo_pago: "Tarjeta empresa",
    ticket_url: "",
    ticket_public_id: "",
    peticion_id: peticionId || "",
    ruta_id: rutaId || "",
    notas: "",
  };

  const [meta, setMeta] = useState({ tipos: [], metodos_pago: [] });
  const [form, setForm] = useState(initial);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(initial);
    api.get("/gastos/meta").then(({ data }) => setMeta(data)).catch(() => {});
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [open, rutaId, peticionId]);

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const { secure_url, public_id } = await uploadToCloudinary(file, "dnf/gastos");
      setForm((f) => ({ ...f, ticket_url: secure_url, ticket_public_id: public_id }));
    } catch (err) {
      toast.error("Error subiendo ticket");
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e?.preventDefault();
    if (!form.importe || parseFloat(form.importe) <= 0) {
      return toast.error("Indica un importe mayor que 0");
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        importe: parseFloat(form.importe),
      };
      await api.post("/gastos", payload);
      toast.success("Gasto registrado");
      onOpenChange?.(false);
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-md max-h-[90vh] overflow-y-auto"
        data-testid="quick-gasto-dialog"
      >
        <DialogHeader>
          <DialogTitle>{title || "Añadir gasto"}</DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">
            Registra un gasto rápido con foto del ticket.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-zinc-400">Fecha</Label>
              <Input type="date" value={form.fecha}
                onChange={(e) => setForm({ ...form, fecha: e.target.value })}
                className="bg-zinc-900 border-zinc-800" data-testid="qg-fecha" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Importe (€) *</Label>
              <Input type="number" step="0.01" min="0" value={form.importe}
                onChange={(e) => setForm({ ...form, importe: e.target.value })}
                placeholder="0,00"
                className="bg-zinc-900 border-zinc-800" data-testid="qg-importe" required autoFocus />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-zinc-400">Tipo</Label>
              <Select value={form.tipo_gasto} onValueChange={(v) => setForm({ ...form, tipo_gasto: v })}>
                <SelectTrigger className="bg-zinc-900 border-zinc-800" data-testid="qg-tipo">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                  {meta.tipos.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Pago</Label>
              <Select value={form.metodo_pago} onValueChange={(v) => setForm({ ...form, metodo_pago: v })}>
                <SelectTrigger className="bg-zinc-900 border-zinc-800" data-testid="qg-metodo">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                  {meta.metodos_pago.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-xs text-zinc-400">Concepto</Label>
            <Input value={form.concepto}
              onChange={(e) => setForm({ ...form, concepto: e.target.value })}
              placeholder="Ej. Gasolina vuelta"
              className="bg-zinc-900 border-zinc-800" data-testid="qg-concepto" />
          </div>

          <div>
            <Label className="text-xs text-zinc-400">Ticket / Factura</Label>
            {form.ticket_url ? (
              <div className="relative inline-block">
                <img src={cloudinaryThumb(form.ticket_url, 200, 200)} alt="ticket"
                  className="w-28 h-28 rounded object-cover border border-zinc-700" />
                <button type="button"
                  onClick={() => setForm({ ...form, ticket_url: "", ticket_public_id: "" })}
                  className="absolute -top-1 -right-1 bg-red-600 rounded-full p-1 text-white">
                  <X className="w-3 h-3" />
                </button>
              </div>
            ) : (
              <label className="cursor-pointer flex items-center justify-center gap-2 w-full p-3 rounded border border-dashed border-zinc-700 bg-zinc-900/40 text-zinc-400 hover:text-zinc-200"
                data-testid="qg-ticket-upload">
                {uploading
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> Subiendo…</>
                  : <><Camera className="w-4 h-4" /> Foto del ticket</>
                }
                <input type="file" accept="image/*" capture="environment" className="hidden"
                  onChange={(e) => handleFile(e.target.files?.[0])} />
              </label>
            )}
          </div>

          <div className="text-[11px] text-zinc-500">
            {form.categoria_gasto === "ruta" && "Imputado a esta ruta · se repartirá entre clientes proporcionalmente."}
            {form.categoria_gasto === "proyecto" && "Imputado directamente a esta campaña."}
            {form.categoria_gasto === "general" && "Gasto general (almacén/oficina)."}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange?.(false)}
              className="bg-transparent border-zinc-700 text-zinc-300">Cancelar</Button>
            <Button type="submit" disabled={saving || uploading}
              className="bg-amber-500 text-zinc-950 hover:bg-amber-400"
              data-testid="qg-save">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Guardando…</> : "Guardar gasto"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
